import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Card, Nav, Tab } from 'react-bootstrap';
import { 
  FaFileContract, 
  FaMagic, 
  FaPlus, 
  FaTrash, 
  FaArrowRight,
  FaSignature,
  FaCalendarAlt,
  FaCheckSquare,
  FaFont,
  FaSave,
  FaPaperPlane
} from 'react-icons/fa';
import axiosInstance from 'helpers/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import html2pdf from 'html2pdf.js';

interface ContractBuilderProps {
  show: boolean;
  onHide: () => void;
  documentUrl?: string;
  documentName?: string;
  clientData?: any;
  onComplete?: (pdfBlob: Blob) => void;
}

interface FieldPlaceholder {
  id: string;
  type: 'text' | 'signature' | 'date' | 'checkbox';
  label: string;
  placeholder: string;
  required: boolean;
}

interface ContractTemplate {
  title: string;
  content: string;
  fields: FieldPlaceholder[];
}

type ContractType = 'purchase' | 'listing' | 'custom';

const ContractBuilder: React.FC<ContractBuilderProps> = ({
  show,
  onHide,
  documentUrl,
  documentName,
  clientData,
  onComplete
}) => {
  const [activeTab, setActiveTab] = useState('write');
  const [contractContent, setContractContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldPlaceholder[]>([]);
  const [contractType, setContractType] = useState<ContractType>('purchase');
  const editorRef = useRef<any>(null);

  // Predefined contract templates
  const contractTemplates: Record<ContractType, ContractTemplate> = {
    purchase: {
      title: 'Residential Purchase Agreement',
      content: `
        <h2 style="text-align: center;">RESIDENTIAL PURCHASE AGREEMENT</h2>
        
        <p>This Purchase Agreement ("Agreement") is entered into on {{date}} between:</p>
        
        <p><strong>BUYER(S):</strong> {{buyer_name}}<br/>
        Address: {{buyer_address}}</p>
        
        <p><strong>SELLER(S):</strong> {{seller_name}}<br/>
        Address: {{seller_address}}</p>
        
        <p><strong>PROPERTY:</strong> {{property_address}}</p>
        
        <h3>1. PURCHASE PRICE</h3>
        <p>The total purchase price for the Property shall be ${'{{purchase_price}}'}, payable as follows:</p>
        <ul>
          <li>Earnest Money Deposit: ${'{{earnest_money}}'}</li>
          <li>Additional Down Payment: ${'{{down_payment}}'}</li>
          <li>Loan Amount: ${'{{loan_amount}}'}</li>
        </ul>
        
        <h3>2. CLOSING</h3>
        <p>Closing shall occur on or before {{closing_date}} at a location designated by the closing agent.</p>
        
        <h3>3. CONTINGENCIES</h3>
        <p>This Agreement is contingent upon:</p>
        <ul>
          <li>{{inspection_checkbox}} Property Inspection ({{inspection_days}} days)</li>
          <li>{{financing_checkbox}} Financing Approval ({{financing_days}} days)</li>
          <li>{{appraisal_checkbox}} Property Appraisal</li>
        </ul>
        
        <h3>4. SIGNATURES</h3>
        <table style="width: 100%; margin-top: 40px;">
          <tr>
            <td style="width: 45%;">
              <p>{{buyer_signature}}<br/>
              Buyer Signature<br/>
              Date: {{buyer_sign_date}}</p>
            </td>
            <td style="width: 10%;"></td>
            <td style="width: 45%;">
              <p>{{seller_signature}}<br/>
              Seller Signature<br/>
              Date: {{seller_sign_date}}</p>
            </td>
          </tr>
        </table>
      `,
      fields: [
        { id: 'date', type: 'date', label: 'Agreement Date', placeholder: '{{date}}', required: true },
        { id: 'buyer_name', type: 'text', label: 'Buyer Name(s)', placeholder: '{{buyer_name}}', required: true },
        { id: 'buyer_address', type: 'text', label: 'Buyer Address', placeholder: '{{buyer_address}}', required: true },
        { id: 'seller_name', type: 'text', label: 'Seller Name(s)', placeholder: '{{seller_name}}', required: true },
        { id: 'seller_address', type: 'text', label: 'Seller Address', placeholder: '{{seller_address}}', required: true },
        { id: 'property_address', type: 'text', label: 'Property Address', placeholder: '{{property_address}}', required: true },
        { id: 'purchase_price', type: 'text', label: 'Purchase Price', placeholder: '{{purchase_price}}', required: true },
        { id: 'earnest_money', type: 'text', label: 'Earnest Money', placeholder: '{{earnest_money}}', required: true },
        { id: 'down_payment', type: 'text', label: 'Down Payment', placeholder: '{{down_payment}}', required: true },
        { id: 'loan_amount', type: 'text', label: 'Loan Amount', placeholder: '{{loan_amount}}', required: true },
        { id: 'closing_date', type: 'date', label: 'Closing Date', placeholder: '{{closing_date}}', required: true },
        { id: 'inspection_checkbox', type: 'checkbox', label: 'Inspection Contingency', placeholder: '{{inspection_checkbox}}', required: false },
        { id: 'inspection_days', type: 'text', label: 'Inspection Days', placeholder: '{{inspection_days}}', required: false },
        { id: 'financing_checkbox', type: 'checkbox', label: 'Financing Contingency', placeholder: '{{financing_checkbox}}', required: false },
        { id: 'financing_days', type: 'text', label: 'Financing Days', placeholder: '{{financing_days}}', required: false },
        { id: 'appraisal_checkbox', type: 'checkbox', label: 'Appraisal Contingency', placeholder: '{{appraisal_checkbox}}', required: false },
        { id: 'buyer_signature', type: 'signature', label: 'Buyer Signature', placeholder: '{{buyer_signature}}', required: true },
        { id: 'buyer_sign_date', type: 'date', label: 'Buyer Sign Date', placeholder: '{{buyer_sign_date}}', required: true },
        { id: 'seller_signature', type: 'signature', label: 'Seller Signature', placeholder: '{{seller_signature}}', required: true },
        { id: 'seller_sign_date', type: 'date', label: 'Seller Sign Date', placeholder: '{{seller_sign_date}}', required: true }
      ]
    },
    listing: {
      title: 'Listing Agreement',
      content: `
        <h2 style="text-align: center;">EXCLUSIVE RIGHT TO SELL LISTING AGREEMENT</h2>
        
        <p>This Listing Agreement is entered into on {{date}} between:</p>
        
        <p><strong>OWNER(S):</strong> {{owner_name}}<br/>
        Property Address: {{property_address}}</p>
        
        <p><strong>BROKER:</strong> {{broker_name}}<br/>
        License #: {{broker_license}}</p>
        
        <h3>1. EXCLUSIVE RIGHT TO SELL</h3>
        <p>Owner grants Broker the exclusive right to sell the Property from {{start_date}} to {{end_date}}.</p>
        
        <h3>2. LISTING PRICE</h3>
        <p>The Property shall be offered for sale at ${'{{listing_price}}'}</p>
        
        <h3>3. COMMISSION</h3>
        <p>Owner agrees to pay Broker a commission of {{commission_rate}}% of the sales price.</p>
        
        <h3>4. SIGNATURES</h3>
        <p>{{owner_signature}}<br/>
        Owner Signature<br/>
        Date: {{owner_sign_date}}</p>
        
        <p>{{broker_signature}}<br/>
        Broker Signature<br/>
        Date: {{broker_sign_date}}</p>
      `,
      fields: [
        { id: 'date', type: 'date', label: 'Agreement Date', placeholder: '{{date}}', required: true },
        { id: 'owner_name', type: 'text', label: 'Owner Name(s)', placeholder: '{{owner_name}}', required: true },
        { id: 'property_address', type: 'text', label: 'Property Address', placeholder: '{{property_address}}', required: true },
        { id: 'broker_name', type: 'text', label: 'Broker Name', placeholder: '{{broker_name}}', required: true },
        { id: 'broker_license', type: 'text', label: 'Broker License #', placeholder: '{{broker_license}}', required: true },
        { id: 'start_date', type: 'date', label: 'Listing Start Date', placeholder: '{{start_date}}', required: true },
        { id: 'end_date', type: 'date', label: 'Listing End Date', placeholder: '{{end_date}}', required: true },
        { id: 'listing_price', type: 'text', label: 'Listing Price', placeholder: '{{listing_price}}', required: true },
        { id: 'commission_rate', type: 'text', label: 'Commission Rate (%)', placeholder: '{{commission_rate}}', required: true },
        { id: 'owner_signature', type: 'signature', label: 'Owner Signature', placeholder: '{{owner_signature}}', required: true },
        { id: 'owner_sign_date', type: 'date', label: 'Owner Sign Date', placeholder: '{{owner_sign_date}}', required: true },
        { id: 'broker_signature', type: 'signature', label: 'Broker Signature', placeholder: '{{broker_signature}}', required: true },
        { id: 'broker_sign_date', type: 'date', label: 'Broker Sign Date', placeholder: '{{broker_sign_date}}', required: true }
      ]
    },
    custom: {
      title: 'Custom Contract',
      content: '<h2 style="text-align: center;">CUSTOM CONTRACT</h2>\n<p>Start writing your custom contract here...</p>',
      fields: []
    }
  };

  useEffect(() => {
    if (contractType) {
      setContractContent(contractTemplates[contractType].content);
      setFields(contractTemplates[contractType].fields);
    }
  }, [contractType]);

  const insertField = (field: FieldPlaceholder) => {
    const quill = editorRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, field.placeholder);
      }
    }
  };

  const generatePDF = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create a container for the PDF content
      const element = document.createElement('div');
      element.innerHTML = contractContent;
      element.style.padding = '40px';
      element.style.fontFamily = 'Arial, sans-serif';
      element.style.fontSize = '12pt';
      element.style.lineHeight = '1.5';

      // Configure PDF options
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: documentName || 'contract.pdf',
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in' as const, format: 'letter' as const, orientation: 'portrait' as const }
      };

      // Generate PDF
      const pdf = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Create form data with field mappings
      const formData = new FormData();
      formData.append('pdf', pdf, documentName || 'contract.pdf');
      formData.append('fields', JSON.stringify(fields.map(f => ({
        name: f.id,
        type: f.type,
        placeholder: f.placeholder,
        required: f.required
      }))));

      // Save to server
      const response = await axiosInstance.post('/contract-templates/save-contract-template', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        if (onComplete) {
          onComplete(pdf);
        }
        
        // Send to Documenso
        await sendToDocumenso(response.data.documentId);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
      setLoading(false);
    }
  };

  const sendToDocumenso = async (documentId: string) => {
    try {
      // For now, just show success message
      // In production, integrate with your Documenso instance
      console.log('Ready to send to Documenso:', documentId);
      
      // Show success notification
      const successMessage = document.createElement('div');
      successMessage.innerHTML = `
        <div style="position: fixed; top: 20px; right: 20px; background: #10b981; color: white; padding: 16px 24px; border-radius: 8px; z-index: 9999;">
          <strong>Success!</strong> Contract is ready for signing. 
          <br/>Document ID: ${documentId}
        </div>
      `;
      document.body.appendChild(successMessage);
      
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 5000);
      
    } catch (err) {
      console.error('Error preparing for Documenso:', err);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" className="contract-builder-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaFileContract className="me-2" />
          Contract Builder - Intelligent Document Creation
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'write')}>
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="write">
                <FaFont className="me-2" />
                Write Contract
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="fields">
                <FaPlus className="me-2" />
                Add Fields
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="preview">
                <FaMagic className="me-2" />
                AI Preview
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="write">
              <Row>
                <Col md={9}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contract Type</Form.Label>
                    <Form.Select 
                      value={contractType}
                      onChange={(e) => setContractType(e.target.value as ContractType)}
                    >
                      <option value="purchase">Purchase Agreement</option>
                      <option value="listing">Listing Agreement</option>
                      <option value="custom">Custom Contract</option>
                    </Form.Select>
                  </Form.Group>

                  <div style={{ height: '500px' }}>
                    <ReactQuill
                      ref={editorRef}
                      theme="snow"
                      value={contractContent}
                      onChange={setContractContent}
                      modules={quillModules}
                      style={{ height: '450px' }}
                    />
                  </div>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Quick Insert Fields</h6>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {fields.map((field) => (
                        <Button
                          key={field.id}
                          variant="outline-primary"
                          size="sm"
                          className="w-100 mb-2 text-start"
                          onClick={() => insertField(field)}
                        >
                          {field.type === 'text' && <FaFont className="me-2" />}
                          {field.type === 'signature' && <FaSignature className="me-2" />}
                          {field.type === 'date' && <FaCalendarAlt className="me-2" />}
                          {field.type === 'checkbox' && <FaCheckSquare className="me-2" />}
                          {field.label}
                        </Button>
                      ))}
                    </Card.Body>
                  </Card>

                  <Alert variant="info" className="mt-3">
                    <small>
                      <strong>Tip:</strong> Click any field to insert it at your cursor position. 
                      Fields will be replaced with actual data when sending for signature.
                    </small>
                  </Alert>
                </Col>
              </Row>
            </Tab.Pane>

            <Tab.Pane eventKey="fields">
              <Row>
                <Col md={8}>
                  <h5>Manage Contract Fields</h5>
                  <p className="text-muted">
                    These fields will become fillable form fields in your contract.
                  </p>

                  {fields.map((field, index) => (
                    <Card key={field.id} className="mb-2">
                      <Card.Body>
                        <Row className="align-items-center">
                          <Col md={3}>
                            <Form.Control
                              type="text"
                              value={field.label}
                              onChange={(e) => {
                                const newFields = [...fields];
                                newFields[index].label = e.target.value;
                                setFields(newFields);
                              }}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Select
                              value={field.type}
                              onChange={(e) => {
                                const newFields = [...fields];
                                newFields[index].type = e.target.value as any;
                                setFields(newFields);
                              }}
                            >
                              <option value="text">Text Field</option>
                              <option value="signature">Signature</option>
                              <option value="date">Date</option>
                              <option value="checkbox">Checkbox</option>
                            </Form.Select>
                          </Col>
                          <Col md={3}>
                            <code>{field.placeholder}</code>
                          </Col>
                          <Col md={2}>
                            <Form.Check
                              type="checkbox"
                              label="Required"
                              checked={field.required}
                              onChange={(e) => {
                                const newFields = [...fields];
                                newFields[index].required = e.target.checked;
                                setFields(newFields);
                              }}
                            />
                          </Col>
                          <Col md={1}>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setFields(fields.filter((_, i) => i !== index));
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}

                  <Button
                    variant="success"
                    onClick={() => {
                      const newField: FieldPlaceholder = {
                        id: `field_${Date.now()}`,
                        type: 'text',
                        label: 'New Field',
                        placeholder: `{{new_field_${fields.length + 1}}}`,
                        required: false
                      };
                      setFields([...fields, newField]);
                    }}
                  >
                    <FaPlus className="me-2" />
                    Add New Field
                  </Button>
                </Col>
                <Col md={4}>
                  <Alert variant="warning">
                    <h6>Field Guidelines</h6>
                    <ul className="mb-0">
                      <li>Use descriptive labels</li>
                      <li>Mark critical fields as required</li>
                      <li>Signature fields will use Documenso</li>
                      <li>Date fields auto-format</li>
                    </ul>
                  </Alert>
                </Col>
              </Row>
            </Tab.Pane>

            <Tab.Pane eventKey="preview">
              <Alert variant="info">
                <FaMagic className="me-2" />
                <strong>AI Intelligence:</strong> The preview shows how your contract will look with sample data. 
                Real data will be populated when sending for signatures.
              </Alert>
              
              <div 
                className="border p-4 bg-white"
                style={{ minHeight: '600px' }}
                dangerouslySetInnerHTML={{ 
                  __html: contractContent
                    .replace(/\{\{date\}\}/g, new Date().toLocaleDateString())
                    .replace(/\{\{buyer_name\}\}/g, 'John & Jane Doe')
                    .replace(/\{\{property_address\}\}/g, '123 Main Street, City, State 12345')
                    .replace(/\{\{purchase_price\}\}/g, '450,000')
                    .replace(/\{\{.*?_signature\}\}/g, '[Signature Field]')
                    .replace(/\{\{.*?_checkbox\}\}/g, '☑')
                    .replace(/\{\{.*?\}\}/g, '[Field]')
                }}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button 
          variant="primary"
          onClick={generatePDF}
          disabled={loading || !contractContent}
        >
          {loading ? 'Generating...' : (
            <>
              <FaPaperPlane className="me-2" />
              Generate & Send to Documenso
            </>
          )}
        </Button>
      </Modal.Footer>
      
      {error && (
        <Alert variant="danger" className="m-3">
          {error}
        </Alert>
      )}
    </Modal>
  );
};

export default ContractBuilder;